import { c as _c } from "react/compiler-runtime";
import { Button, Input, Popover, PopoverContent, PopoverTrigger, TooltipTrigger, Tooltip, cn, TooltipContent } from '@newfront-insurance/core-ui/v2';
import { ChevronDown, XIcon } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { SearchRow } from './search-row';
import { type NovaSearchAccountResult, type SelectedAccount, useAccountSearch } from './use-account-search';
export interface AccountSelectorProps {
  selectedAccount: SelectedAccount;
  onSelectAccount: (account: SelectedAccount) => void;
  loggedInUserUuid: string;
  userScopes: string[];
  disabled?: boolean;
  showAnimation?: boolean;
  className?: string;
  width?: number;
  placeholder?: string;
}
export function AccountSelector(t0) {
  const $ = _c(72);
  const {
    width,
    placeholder: t1,
    selectedAccount,
    onSelectAccount,
    loggedInUserUuid,
    userScopes,
    disabled: t2,
    showAnimation: t3,
    className
  } = t0;
  const placeholder = t1 === undefined ? "Click to select an account" : t1;
  const disabled = t2 === undefined ? false : t2;
  const showAnimation = t3 === undefined ? false : t3;
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  let t4;
  if ($[0] !== loggedInUserUuid || $[1] !== userScopes) {
    t4 = {
      loggedInUserUuid,
      userScopes
    };
    $[0] = loggedInUserUuid;
    $[1] = userScopes;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const {
    handleSearch,
    handleReset,
    isLoading,
    results,
    error
  } = useAccountSearch(t4);
  const selectedAccountLabel = selectedAccount?.label ?? placeholder;
  let t5;
  if ($[3] !== handleSearch) {
    t5 = value => {
      setSearchValue(value);
      handleSearch(value);
    };
    $[3] = handleSearch;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleInputChange = t5;
  let t6;
  if ($[5] !== handleReset || $[6] !== onSelectAccount) {
    t6 = account => {
      onSelectAccount(account);
      setIsOpen(false);
      setSearchValue("");
      handleReset();
    };
    $[5] = handleReset;
    $[6] = onSelectAccount;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleAccountSelect = t6;
  let t7;
  if ($[8] !== disabled || $[9] !== handleReset) {
    t7 = open => {
      if (disabled && open) {
        return;
      }
      setIsOpen(open);
      if (!open) {
        setSearchValue("");
        handleReset();
      }
    };
    $[8] = disabled;
    $[9] = handleReset;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleOpenChange = t7;
  const t8 = !selectedAccount;
  const t9 = showAnimation && !disabled;
  let t10;
  if ($[11] !== className || $[12] !== t8 || $[13] !== t9) {
    t10 = cn("flex-basis-[300px] relative max-w-[300px] min-w-0 shrink justify-between px-4 pr-10 text-left font-normal sm:min-w-[300px]", {
      "text-muted-foreground": t8,
      "border-blue-300 bg-blue-50/50": t9
    }, className);
    $[11] = className;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== width) {
    t11 = {
      width
    };
    $[15] = width;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== selectedAccountLabel) {
    t12 = <div className="flex min-w-0 flex-1 items-center gap-1.5"><span className="text-steel-500 truncate text-ellipsis">{selectedAccountLabel}</span></div>;
    $[17] = selectedAccountLabel;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChevronDown className="absolute top-3 right-2 h-4 w-4 flex-shrink-0" />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== disabled || $[21] !== selectedAccountLabel || $[22] !== t10 || $[23] !== t11 || $[24] !== t12) {
    t14 = <Button variant="secondary" size="default" disabled={disabled} className={t10} style={t11} title={selectedAccountLabel}>{t12}{t13}</Button>;
    $[20] = disabled;
    $[21] = selectedAccountLabel;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== disabled || $[27] !== handleAccountSelect || $[28] !== selectedAccount) {
    t15 = selectedAccount && !disabled && <Button variant="transparent" className="absolute top-0 right-6 p-1" onClick={e => {
      e.stopPropagation();
      e.preventDefault();
      handleAccountSelect(null);
    }}><XIcon width={16} height={16} /></Button>;
    $[26] = disabled;
    $[27] = handleAccountSelect;
    $[28] = selectedAccount;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t14 || $[31] !== t15) {
    t16 = <TooltipTrigger asChild={true}><div className="relative w-fit min-w-0">{t14}{t15}</div></TooltipTrigger>;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = !disabled;
  let t18;
  if ($[33] !== t17) {
    t18 = cn({
      hidden: t17
    });
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <p>Create a new chat to change the account</p>;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t18) {
    t20 = <TooltipContent side="bottom" className={t18}>{t19}</TooltipContent>;
    $[36] = t18;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t16 || $[39] !== t20) {
    t21 = <PopoverTrigger asChild={true}><div className="w-fit min-w-0 sm:mx-auto"><Tooltip>{t16}{t20}</Tooltip></div></PopoverTrigger>;
    $[38] = t16;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== handleInputChange) {
    t22 = e_0 => handleInputChange(e_0.target.value);
    $[41] = handleInputChange;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== searchValue || $[44] !== t22) {
    t23 = <div className="p-2"><Input type="search" placeholder="Search for account to chat with..." value={searchValue} onChange={t22} autoFocus={true} /></div>;
    $[43] = searchValue;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== error) {
    t24 = error && <div className="text-fire-500 p-3 text-sm">{error.data.errorMessage}</div>;
    $[46] = error;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== isLoading) {
    t25 = isLoading && <div className="text-muted-foreground p-3 text-sm">Searching accounts...</div>;
    $[48] = isLoading;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== error || $[51] !== isLoading || $[52] !== results || $[53] !== searchValue) {
    t26 = !isLoading && !error && searchValue && (!results || results.length === 0) && <div className="text-muted-foreground p-3 text-sm">No accounts found</div>;
    $[50] = error;
    $[51] = isLoading;
    $[52] = results;
    $[53] = searchValue;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== handleAccountSelect || $[56] !== results || $[57] !== selectedAccount?.uuid) {
    t27 = results?.map(account_0 => <SearchRow key={account_0.uuid} account={account_0} isSelected={selectedAccount?.uuid === account_0.uuid} onClick={() => handleAccountSelect(account_0)} />);
    $[55] = handleAccountSelect;
    $[56] = results;
    $[57] = selectedAccount?.uuid;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t24 || $[60] !== t25 || $[61] !== t26 || $[62] !== t27) {
    t28 = <div className="max-h-60 overflow-y-auto">{t24}{t25}{t26}{t27}</div>;
    $[59] = t24;
    $[60] = t25;
    $[61] = t26;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t23 || $[65] !== t28) {
    t29 = <PopoverContent className="p-0 lg:w-[300px]" align="start">{t23}{t28}</PopoverContent>;
    $[64] = t23;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== handleOpenChange || $[68] !== isOpen || $[69] !== t21 || $[70] !== t29) {
    t30 = <Popover open={isOpen} onOpenChange={handleOpenChange}>{t21}{t29}</Popover>;
    $[67] = handleOpenChange;
    $[68] = isOpen;
    $[69] = t21;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  return t30;
}